import { IconOpenInNew } from "@/assets/icons";
import { ButtonNew } from "@/modules/irohana-components/elements/ButtonNew";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { UserInitialAvatar } from "@/features/user-settings/components/UserInitialAvatar";
import { TUserSettingDetail } from "@/api/services/main/user-setting/dtos/get-user-setting.dto";
import { TUserSettingForm } from "@/features/user-settings/types/user-setting-form";
import { divider, text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { Divider, Stack, Typography } from "@mui/material";
import { FC, ReactNode } from "react";
import { useFormContext } from "react-hook-form";

type TProfileSectionProps = {
  detail?: TUserSettingDetail;
  readOnly?: boolean;
  onChangeEmail?: () => void;
  onChangePassword?: () => void;
};

const ViewRow: FC<{ label: string; value?: ReactNode; action?: ReactNode; showDivider?: boolean }> = ({ label, value, action, showDivider = true }) => (
  <>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={spacing.sm} py={spacing.sm}>
      <Stack gap={0.5} minWidth={0}>
        <Typography variant="cap12" color={text.secondary}>
          {label}
        </Typography>
        {value != null && value !== "" && (
          <Typography variant="body14" color={text.primary} sx={{ wordBreak: "break-all" }}>
            {value}
          </Typography>
        )}
      </Stack>
      {action}
    </Stack>
    {showDivider && <Divider sx={{ borderColor: divider.middle }} />}
  </>
);

const EditRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Stack gap={0.5}>
    <Typography variant="cap12" color={text.secondary}>
      {label}
    </Typography>
    {children}
  </Stack>
);

export const ProfileSection: FC<TProfileSectionProps> = ({ detail, readOnly = false, onChangeEmail, onChangePassword }) => {
  const formContext = useFormContext<TUserSettingForm>();

  return (
    <Stack gap={spacing.md}>
      <Stack direction="row" alignItems="center" gap={spacing.sm}>
        <UserInitialAvatar fullName={detail?.userFullName} />
        <Typography variant="body14Semi" color={text.primary}>
          {detail?.userFullName}
        </Typography>
      </Stack>

      {readOnly ? (
        <Stack>
          <ViewRow label="表示名" value={detail?.userFullName} />
          <ViewRow
            label="メールアドレス"
            value={detail?.userEmail}
            action={
              <ButtonNew variant="secondary" size="sm" onClick={onChangeEmail} endIcon={<IconOpenInNew />}>
                メールアドレス変更
              </ButtonNew>
            }
          />
          <ViewRow
            label="パスワード"
            showDivider={false}
            action={
              <ButtonNew variant="secondary" size="sm" onClick={onChangePassword} endIcon={<IconOpenInNew />}>
                パスワード変更
              </ButtonNew>
            }
          />
        </Stack>
      ) : (
        <Stack gap={spacing.sm}>
          <EditRow label="表示名">
            <FormField name="userFullName" noLabel control={formContext?.control}>
              <Input fullWidth clearable placeholder="表示名を入力してください" />
            </FormField>
          </EditRow>
          <EditRow label="メールアドレス">
            <Input fullWidth nonControl readonly value={detail?.userEmail ?? ""} />
          </EditRow>
        </Stack>
      )}
    </Stack>
  );
};
